import { LitElement, html, nothing, unsafeCSS, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import styles from './csv-import-cancel-confirm-modal.css?inline';

import '@inmar/component-lib';

// ============================================================================
// Types
// ============================================================================

/**
 * View states for the cancel confirmation overlay.
 * - 'cancel-import': Standard cancel confirmation (preview step)
 * - 'download-report': Download report warning (confirm step with undownloaded report)
 * - 'download-report-success': Download succeeded, user can now close
 */
export type CancelConfirmView =
  | 'cancel-import'
  | 'download-report'
  | 'download-report-success';

export interface CancelConfirmYesEvent {
  element: CsvImportCancelConfirmModal;
}

export interface CancelConfirmNoEvent {
  element: CsvImportCancelConfirmModal;
}

export interface CancelConfirmDownloadEvent {
  element: CsvImportCancelConfirmModal;
}

// ============================================================================
// Global augmentations
// ============================================================================

declare global {
  interface HTMLElementEventMap {
    'cancel-confirm-yes': CustomEvent<CancelConfirmYesEvent>;
    'cancel-confirm-no': CustomEvent<CancelConfirmNoEvent>;
    'cancel-confirm-download': CustomEvent<CancelConfirmDownloadEvent>;
  }
  interface HTMLElementTagNameMap {
    'csv-import-cancel-confirm-modal': CsvImportCancelConfirmModal;
  }
}

/**
 * Cancel confirmation modal for the csv-importer component.
 *
 * Displays a warning overlay when the user attempts to close the import workflow,
 * asking them to confirm before discarding progress.
 *
 * @fires cancel-confirm-yes - Emitted when user confirms cancellation ("Yes, I'm sure" or "Yes, close").
 *   Detail: `{ element: CsvImportCancelConfirmModal }`
 * @fires cancel-confirm-no - Emitted when user declines cancellation ("No, continue" or X button or Escape).
 *   Detail: `{ element: CsvImportCancelConfirmModal }`
 * @fires cancel-confirm-download - Emitted when user clicks "Download Final Error Report" (download-report view only).
 *   Detail: `{ element: CsvImportCancelConfirmModal }`
 */
@customElement('csv-import-cancel-confirm-modal')
export class CsvImportCancelConfirmModal extends LitElement {
  static readonly styles = [unsafeCSS(styles)];

  // ============================================================================
  // Properties
  // ============================================================================

  /** Whether the cancel confirmation modal is visible. */
  @property({ type: Boolean, reflect: true })
  open = false;

  /** Title displayed in the modal header (matches workflow shell title). */
  @property({ type: String, attribute: 'modal-title' })
  modalTitle = 'Import';

  /**
   * Which variant to display.
   * - 'cancel-import': Standard cancel confirmation
   * - 'download-report': Download report warning
   */
  @property({ type: String, reflect: true })
  view: CancelConfirmView = 'cancel-import';

  // ============================================================================
  // Event handlers
  // ============================================================================

  /**
   * Handles X button click or Escape key press.
   * Dispatches cancel-confirm-no to dismiss the overlay without confirming.
   */
  private _onXButtonOrEscape() {
    this.dispatchEvent(
      new CustomEvent<CancelConfirmNoEvent>('cancel-confirm-no', {
        bubbles: true,
        composed: true,
        detail: { element: this },
      }),
    );
  }

  /**
   * Handles "Yes, I'm sure" (cancel-import view) or "Yes, close" (download-report view) click.
   * Dispatches cancel-confirm-yes to confirm the action.
   */
  private _onYes() {
    this.dispatchEvent(
      new CustomEvent<CancelConfirmYesEvent>('cancel-confirm-yes', {
        bubbles: true,
        composed: true,
        detail: { element: this },
      }),
    );
  }

  /**
   * Handles "No, continue importing" click.
   * Dispatches cancel-confirm-no to dismiss the overlay without confirming.
   */
  private _onNo() {
    this.dispatchEvent(
      new CustomEvent<CancelConfirmNoEvent>('cancel-confirm-no', {
        bubbles: true,
        composed: true,
        detail: { element: this },
      }),
    );
  }

  /**
   * Handles "Download Final Error Report" click (download-report view only).
   * Dispatches cancel-confirm-download to trigger the download.
   */
  private _onDownload() {
    this.dispatchEvent(
      new CustomEvent<CancelConfirmDownloadEvent>('cancel-confirm-download', {
        bubbles: true,
        composed: true,
        detail: { element: this },
      }),
    );
  }

  // ============================================================================
  // View content
  // ============================================================================

  /**
   * Returns view-specific content for the modal body and footer.
   *
   * This approach allows future views to have completely different body structures
   * (forms, lists, different icons) without modifying the render method.
   */
  private _getViewContent(): { body: TemplateResult; footer: TemplateResult } {
    switch (this.view) {
      case 'cancel-import':
        return {
          body: html`
            <hcds-icon
              name="diamond-exclamation"
              family="classic"
              icon-style="light"
              class="warning-icon"
              aria-hidden="true"
            ></hcds-icon>
            <p class="warning-message">
              Are you sure you want to cancel this import?
            </p>
          `,
          footer: html`
            <inmar-hc-button
              label="Yes, I'm sure"
              variant="primary"
              @button-click=${this._onYes}
            ></inmar-hc-button>
            <inmar-hc-button
              label="No, continue importing"
              variant="tertiary"
              @button-click=${this._onNo}
            ></inmar-hc-button>
          `,
        };
      case 'download-report':
        return {
          body: html`
            <hcds-icon
              name="diamond-exclamation"
              family="classic"
              icon-style="light"
              class="warning-icon"
              aria-hidden="true"
            ></hcds-icon>
            <p class="warning-message">
              Are you sure you want to leave without downloading your final
              error report?
            </p>
          `,
          footer: html`
            <inmar-hc-button
              label="Yes, close"
              variant="primary"
              @button-click=${this._onYes}
            ></inmar-hc-button>
            <inmar-hc-button
              label="Download Final Error Report"
              variant="secondary"
              @button-click=${this._onDownload}
            >
              <hcds-icon
                slot="left-icon"
                name="download"
                family="classic"
                icon-style="light"
              ></hcds-icon>
            </inmar-hc-button>
          `,
        };
      case 'download-report-success':
        return {
          body: html`
            <hcds-icon
              name="check-circle"
              family="classic"
              icon-style="light"
              class="success-icon"
              aria-hidden="true"
            ></hcds-icon>
            <p class="warning-message">
              Your error report has been downloaded. You can now leave this
              screen.
            </p>
          `,
          footer: html`
            <inmar-hc-button
              label="Close"
              variant="primary"
              @button-click=${this._onYes}
            ></inmar-hc-button>
          `,
        };
    }
  }

  // ============================================================================
  // Render
  // ============================================================================

  override render() {
    // NOTE: Using early-return pattern because inmar-hc-modal unmounts DOM on
    // .open=false. If the modal is replaced with one that keeps DOM mounted,
    // switch to .open=${this.open} pattern for better a11y (avoids focus jumps
    // on remount).
    if (!this.open) return nothing;

    const content = this._getViewContent();

    return html`
      <inmar-hc-modal
        .open=${true}
        modal-title=${this.modalTitle}
        size="md"
        @modal-closed=${this._onXButtonOrEscape}
      >
        <div slot="body" class="cancel-confirm-body">${content.body}</div>
        <div slot="footer" class="cancel-confirm-footer">${content.footer}</div>
      </inmar-hc-modal>
    `;
  }
}
